import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { ErrorStateMatcher, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppComponent, AppRoutingModule } from '@app/index';
import { PageNotFoundModule, ManagerModule } from '@modules/index';
import { CookieService } from 'ngx-cookie-service';
import { HIGHLIGHT_OPTIONS } from 'ngx-highlightjs';
import { environment } from '../environments/environment';
import { Z5HttpShareInterceptor, GATrackingModule, FormAsyncValidatorsModule } from '@tcp13equals2/z5-lib';
import Bugsnag from '@bugsnag/js';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';

import { SharedModule } from './shared/shared.module';
import { DirtyErrorStateMatcher } from './services/forms/dirty-error-state-matcher';
import { AuthInterceptor } from './services/auth-interceptor/auth.interceptor';
import { createErrorHandlerFactory, ERROR_LOGGER } from './services/error';
import { config } from '../environments/config';
import { getAuthUser } from '../shared/utilities';
import { EnvironmentConfiguration } from '../environments/environment.models';

const modules = [
	AppRoutingModule,
	ManagerModule,
	PageNotFoundModule,
	SharedModule,
	MatMomentDateModule,
	HttpClientModule,
	BrowserAnimationsModule,
	GATrackingModule.forRoot(),
	FormAsyncValidatorsModule.forRoot({
		api: environment.sis
	})
];

export function highlightCoreLibraryLoader() {
	return import('highlight.js/lib/core');
}
export function highlightLineNumbersLoader() {
	return import('highlightjs-line-numbers.js');
}
export function highlightJavascript() {
	return import('highlight.js/lib/languages/javascript');
}
export function highlightCss() {
	return import('highlight.js/lib/languages/css');
}
export function highlightXml() {
	return import('highlight.js/lib/languages/xml');
}

let errorHandler = createErrorHandlerFactory();
let errorLogger = null;

if (environment.configuration === EnvironmentConfiguration.staging || environment.configuration === EnvironmentConfiguration.production) {
	const bugsnagConfig = {
		apiKey: config.bugsnagKey,
		enabledReleaseStages: [EnvironmentConfiguration.production, EnvironmentConfiguration.staging],
		releaseStage: environment.configuration,
		collectUserIp: false,
		onError: (event) => {
			const user = getAuthUser();

			if (user?.id) event.setUser(user.id);
		},
		redactedKeys: [
			/^email$/i,
			/^password$/i,
			/^clientSecret$/i,
			/^firstname$/i,
			/^lastname$/i,
			/^addresses$/i,
			/^billingCountry$/i,
			/^country$/i,
			/^dob$/i,
			/^dobDayOfMonth$/i,
			/^dobDayOfYear$/i,
			/^dobMonth$/i,
			/^dobYear$/i,
			/^gender$/i,
			/^locality$/i,
			/^state$/i,
			/^tns$/i,
			/^_name$/i,
			/^_billingCountry-display$/i,
			/^_dob-display$/i,
			/^_gender-display$/i,
			/^refresh_token$/i,
			/^access_token$/i,
			/^authorization$/i,
			/^token$/i,
		]
	};

	if (config.version) bugsnagConfig['appVersion'] = config.version;

	Bugsnag.start(bugsnagConfig);

	errorHandler = createErrorHandlerFactory(new BugsnagErrorHandler());
	errorLogger = {
		error: (...args: any[]) => Bugsnag.notify(new Error(...args))
	};
}

@NgModule({
	declarations: [AppComponent],
	imports: [...modules],
	providers: [
		{ provide: ErrorHandler, useFactory: errorHandler },
		{ provide: ERROR_LOGGER, useValue: errorLogger },
		CookieService,
		{ provide: MAT_DATE_LOCALE, useValue: environment.defaultLanguage },
		{ provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
		{
			provide: HTTP_INTERCEPTORS,
			useClass: Z5HttpShareInterceptor,
			multi: true
		},
		{ provide: ErrorStateMatcher, useClass: DirtyErrorStateMatcher },
		{
			provide: HIGHLIGHT_OPTIONS,
			useValue: {
				coreLibraryLoader: highlightCoreLibraryLoader,
				lineNumbersLoader: highlightLineNumbersLoader, // Optional, only if you want the line numbers
				languages: {
					javascript: highlightJavascript,
					css: highlightCss,
					xml: highlightXml
				}
			}
		},
		{
			provide: MAT_DATE_FORMATS,
			useValue: {
				parse: {
					dateInput: ['DD/MM/YYYY']
				},
				display: {
					dateInput: 'DD/MM/YYYY',
					monthYearLabel: 'MMM YYYY',
					monthYearA11yLabel: 'MMMM YYYY'
				}
			}
		},
	],
	bootstrap: [AppComponent]
})
export class AppModule {}
